/**
 * Post-login landing route for the Auth0 redirect (`/callback`).
 *
 * What: Shows a spinner while the code exchange and role fetch settle, then
 * sends the user to their role-default page; surfaces a retryable error view
 * if the exchange fails (stale state, double-submit, blocked storage).
 * Why: Logins that carry an explicit destination navigate straight there via
 * `appState.returnTo` (see `Auth0ProviderWithNavigate`); this route only
 * handles logins without one, which need the role loaded before a
 * destination exists. Navigating blindly to `/` dumped signed-in users on
 * the marketing landing page.
 * When: Update when changing post-login routing, role defaults, or the
 * `/callback` error handling.
 */
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Button, Stack, Typography } from '@mui/material';

import {
  buildGuidedSignupLoginOptions,
  isGuidedSignupCallbackError,
} from '@/auth/guidedSignupAuth';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import { useAuth } from '@/hooks/useAuth';
import { getDefaultRedirectPath } from '@/utils/accessControl';
import { landingStateFor } from '@/utils/teacherEventSource';

function AuthCallback() {
  const { isAuthenticated, isLoading, error, loginWithRedirect } = useAuth0();
  const { user, userRole, isRoleLoaded } = useAuth();
  const navigate = useNavigate();
  const retryGuidedSignup = isGuidedSignupCallbackError(error);

  const handleRetry = () => {
    if (retryGuidedSignup) {
      void loginWithRedirect(buildGuidedSignupLoginOptions());
      return;
    }
    void loginWithRedirect();
  };

  useEffect(() => {
    if (isLoading || error) return;

    // Pre-appState bundles stored the destination here; clear any leftover
    // key so it can't shadow the current mechanism.
    localStorage.removeItem('returnTo');

    if (!isAuthenticated) {
      // Direct visit without a session (bookmarked /callback, cancelled
      // login) — nothing to resume, back to the homepage.
      navigate('/', { replace: true });
      return;
    }

    if (isRoleLoaded) {
      const destination = getDefaultRedirectPath(userRole, user);
      // The Students class page records this open as a landing, not a choice.
      navigate(destination, {
        replace: true,
        state: landingStateFor(destination),
      });
    }
  }, [
    isLoading,
    error,
    isAuthenticated,
    isRoleLoaded,
    userRole,
    user,
    navigate,
  ]);

  if (error) {
    return (
      <>
        <Helmet>
          <title>Sign-in Problem | DeepThink</title>
        </Helmet>
        <Box
          sx={{
            minHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
          }}
        >
          <Stack spacing={2} alignItems="center" sx={{ maxWidth: 420 }}>
            <Typography variant="h5" component="h1" fontWeight={700}>
              We couldn't finish signing you in
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center">
              {error.message ||
                'The sign-in attempt expired or was interrupted.'}{' '}
              Trying again usually fixes this.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button variant="contained" color="primary" onClick={handleRetry}>
                Try again
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => navigate('/', { replace: true })}
              >
                Back to home
              </Button>
            </Stack>
          </Stack>
        </Box>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Signing In | DeepThink</title>
      </Helmet>
      <LoadingSpinner />
    </>
  );
}

export default AuthCallback;
